import {
  CLOUD_LABELS,
  CONTENT_TYPE_LABELS,
  LENS_LABELS,
  RELEASE_STATUS_LABELS,
  TOPIC_LABELS,
  hasVerifiedAvailability,
  type Lens,
  type Story,
} from "../../shared/model.ts";
import { formatDate } from "../lib/briefing.ts";
import { TopicIcon } from "./TopicIcon.tsx";
import { BookmarkIcon, CardActions, Pill, SampleBadge } from "./ui.tsx";
import { WhyItMatters } from "./WhyItMatters.tsx";
import { SummaryText } from "./SummaryText.tsx";

interface Props {
  story: Story;
  lens: Lens;
  saved: boolean;
  onToggleSave(): void;
  active?: boolean;
  headingLevel?: "h1" | "h2" | "h3";
}

export function StoryMeta({ story }: { story: Story }) {
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink-soft">
      <span>{story.sourceName}</span>
      <span aria-hidden="true">·</span>
      <span>{story.publishedAt ? <time dateTime={new Date(story.publishedAt).toISOString()}>{formatDate(story.publishedAt)}</time> : "Date not provided"}</span>
      <span aria-hidden="true">·</span>
      <span title="Estimated reading time for the original source">
        ~{story.estimatedReadMinutes} min read <span className="italic">(estimate)</span>
      </span>
    </p>
  );
}

export function VerifiedLabels({ story }: { story: Story }) {
  if (!hasVerifiedAvailability(story)) return null;
  const showStatus = story.releaseStatus !== "unknown";
  if (!showStatus && !story.clouds.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Verified availability">
      {showStatus && <Pill tone="verified">{RELEASE_STATUS_LABELS[story.releaseStatus]}</Pill>}
      {story.clouds.map((c) => (
        <Pill key={c}>{CLOUD_LABELS[c]}</Pill>
      ))}
      <a href={story.availabilitySourceUrl!} target="_blank" rel="noopener noreferrer" className="text-xs text-ink underline decoration-line-strong underline-offset-[3px] hover:decoration-ink">
        verified {formatDate(story.availabilityVerifiedAt!)}
      </a>
    </div>
  );
}

export function SaveButton({ story, saved, onToggle, compact = false }: { story: { title: string }; saved: boolean; onToggle(): void; compact?: boolean }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={saved ? `Saved: remove “${story.title}” from Reading Basket` : `Read later: save “${story.title}” to Reading Basket`}
      // Secondary button in both states; the filled bookmark and the label say "Saved".
      className={`inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-line-strong px-3.5 text-sm font-semibold text-ink transition-colors hover:bg-sand ${saved ? "bg-sand" : "bg-card"} ${compact ? "px-3" : ""}`}
    >
      <BookmarkIcon filled={saved} />
      {saved ? "Saved" : "Read later"}
    </button>
  );
}

export function StoryCard({ story, lens, saved, onToggleSave, active = false, headingLevel = "h3" }: Props) {
  const H = headingLevel;
  // As the page (story view) it is a reading layout: 16px text, about 70 characters per line (60ch).
  const reading = headingLevel === "h1";
  const headingId = `story-${story.id}-title`;
  return (
    <article
      aria-labelledby={headingId}
      className={`flex h-full flex-col gap-4 rounded-xl border bg-card p-5 transition-[box-shadow,border-color] duration-200 sm:p-6 ${
        active ? "border-ink/25 shadow-[0_18px_40px_-24px_rgb(38_38_43/0.45)]" : "border-line"
      }`}
    >
      <header className="flex items-start gap-3">
        <TopicIcon topic={story.primaryTopic} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">
            {TOPIC_LABELS[story.primaryTopic]}
            <span className="font-normal text-ink-soft"> · {CONTENT_TYPE_LABELS[story.contentType]}</span>
          </p>
          {story.isSample && <SampleBadge className="mt-1" />}
        </div>
        {/* Same corner as the Browse cards. */}
        <CardActions storyId={story.id} title={story.title} url={story.canonicalUrl} saved={saved} onToggleSave={onToggleSave} className="-mr-2 -mt-2 shrink-0" />
      </header>

      <H id={headingId} className={`font-display font-semibold leading-snug tracking-[-0.01em] text-balance ${reading ? "max-w-[34ch] text-3xl" : "text-xl"}`}>
        {story.title}
      </H>

      <div className="flex-1 space-y-3">
        <div className={`leading-relaxed text-ink ${reading ? "max-w-[60ch] text-base" : "text-sm"}`}>
          <span className="sr-only">{story.summaryProvenance === "source_excerpt" ? "From the source: " : `${LENS_LABELS[lens]} summary: `}</span>
          <SummaryText text={story.summaries[lens]} />
        </div>
        <WhyItMatters story={story} />
      </div>

      <VerifiedLabels story={story} />
      <StoryMeta story={story} />

    </article>
  );
}
